import { useEffect, useMemo, useState } from "react";
import History, { pushHistory } from "./History";
import { motion } from "framer-motion";
import { Receipt, Clock3, CircleDollarSign, Percent, MessageCircle, Send, Link2, Check, ImageDown } from "lucide-react";
import { useI18n } from "../i18n";
import { useSoundOn, useResultSound } from "../sound";
import SoundControls from "./SoundControls";
import { shareWhatsApp, shareTelegram, copyText, summaryRefund, IMG_LABEL } from "../share";
import { downloadResultImage } from "../resultImage";
import { AnimatedRupiah } from "./AnimatedNumber";
import SisaHari from "./SisaHari";
import Tilt from "./Tilt";

const digits = (s) => s.replace(/\D/g, "");
const fmt = (n) => new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(n);
const rpInput = (s) => {
    const d = digits(s);
    return d ? fmt(+d) : "";
};

function Field({ label, hint, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</label>
            {children}
            {hint && <p className="text-[10px] leading-relaxed text-zinc-600">{hint}</p>}
        </div>
    );
}

const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-medium text-white placeholder-zinc-600 outline-none backdrop-blur-sm transition-colors duration-200 focus:border-brand-violet/60 focus:bg-white/[0.06]";

const shareBtn =
    "flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-zinc-300 transition-colors duration-200 hover:border-white/40 hover:text-white";

export default function Calculator() {
    const { t, lang } = useI18n();
    const [tab, setTab] = useState("refund");
    const [nama, setNama] = useState("");
    const [harga, setHarga] = useState("");
    const [total, setTotal] = useState("");
    const [sisa, setSisa] = useState("");
    const [rate, setRate] = useState(0.7);
    const [copied, setCopied] = useState(false);
    const [soundOn] = useSoundOn();

    const hargaN = +digits(harga);
    const totalN = +digits(total);
    const sisaN = +digits(sisa);

    const hasil = useMemo(() => {
        if (hargaN > 0 && totalN > 0 && sisaN >= 0) return Math.round((hargaN / totalN) * sisaN * rate);
        return null;
    }, [hargaN, totalN, sisaN, rate]);

    const perHari = hargaN > 0 && totalN > 0 ? Math.round(hargaN / totalN) : 0;
    const pct = totalN > 0 ? Math.min(100, (sisaN / totalN) * 100) : 0;
    const shareText =
        hasil !== null ? summaryRefund(t, { nama, harga: hargaN, total: totalN, sisa: sisaN, rate, hasil }) : "";
    const unduhGambar = () =>
        hasil !== null &&
        downloadResultImage({
            filename: "refundaja-refund.png",
            title: `${t.r3}${nama ? " — " + nama : ""}`,
            big: "Rp " + fmt(hasil),
            lines: [
                `${t.f2}: Rp ${fmt(hargaN)}`,
                `${t.f4}: ${totalN} · ${t.f3}: ${sisaN}`,
                `${t.f5}: ${Math.round(rate * 100)}%`,
            ],
        });
    useResultSound(hasil, soundOn);
    useEffect(() => {
        const h = (e) => {
            setNama(e.detail);
            setTab("refund");
            document.getElementById("kalkulator")?.scrollIntoView({ behavior: "smooth" });
        };
        window.addEventListener("refundaja-isi-nama", h);
        return () => window.removeEventListener("refundaja-isi-nama", h);
    }, []);
    useEffect(() => {
        if (hasil === null) return;
        const id = setTimeout(
            () => pushHistory({ nama, harga: hargaN, total: totalN, sisa: sisaN, rate, hasil }),
            900
        );
        return () => clearTimeout(id);
    }, [hasil]);

    return (
        <section id="kalkulator" className="relative scroll-mt-24 py-24 sm:py-32" data-testid="section-kalkulator">
            <div className="absolute left-1/2 top-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-violet/10 blur-[160px]" />
            <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.7 }}
                    className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between"
                >
                    <div>
                        <p className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-brand-violet">
                            {t.ke}
                        </p>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
                            {t.kt1} <span className="text-gradient">{t.kt2}</span>
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm leading-relaxed text-zinc-400">{t.kd}</p>
                </motion.div>

                {/* Tabs */}
                <div className="mx-auto mb-8 flex w-fit gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-1.5 backdrop-blur-xl" data-testid="kalkulator-tabs">
                    {[
                        { id: "refund", label: t.t1, tid: "tab-hitung-refund" },
                        { id: "sisa", label: t.t2, tid: "tab-hitung-sisa-hari" },
                    ].map((tb) => (
                        <button
                            key={tb.id}
                            onClick={() => setTab(tb.id)}
                            data-testid={tb.tid}
                            className={`relative rounded-xl px-5 py-2.5 text-xs font-bold transition-colors duration-200 sm:text-sm ${
                                tab === tb.id ? "text-[#0a0713]" : "text-zinc-400 hover:text-zinc-200"
                            }`}
                        >
                            {tab === tb.id && (
                                <motion.span
                                    layoutId="tab-pill"
                                    className="absolute inset-0 rounded-xl bg-gradient-to-r from-brand-coral via-brand-violet to-brand-blue"
                                    transition={{ type: "spring", stiffness: 320, damping: 28 }}
                                />
                            )}
                            <span className="relative z-10">{tb.label}</span>
                        </button>
                    ))}
                </div>

                {tab === "refund" ? (
                    <div className="grid gap-6 lg:grid-cols-2">
                        {/* Inputs */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ duration: 0.7 }}
                            className="glass flex flex-col gap-5 rounded-3xl p-6 sm:p-8"
                            data-testid="panel-input"
                        >
                            <Field label={t.f1}>
                                <input
                                    data-testid="input-nama"
                                    value={nama}
                                    onChange={(e) => setNama(e.target.value)}
                                    placeholder={t.f1p}
                                    className={inputCls}
                                />
                            </Field>

                            <Field label={t.f2} hint={t.f2h}>
                                <div className="relative flex items-center">
                                    <span className="pointer-events-none absolute left-4 font-mono text-sm font-bold text-zinc-500">
                                        Rp
                                    </span>
                                    <input
                                        data-testid="input-harga"
                                        inputMode="numeric"
                                        value={harga}
                                        onChange={(e) => setHarga(rpInput(e.target.value))}
                                        placeholder="0"
                                        className={`${inputCls} pl-11 font-mono`}
                                    />
                                </div>
                            </Field>

                            <Field label={t.f3}>
                                <input
                                    data-testid="input-sisa"
                                    inputMode="numeric"
                                    value={sisa}
                                    onChange={(e) => setSisa(digits(e.target.value))}
                                    placeholder="Contoh: 20"
                                    className={`${inputCls} font-mono`}
                                />
                            </Field>

                            <Field label={t.f4}>
                                <input
                                    data-testid="input-total"
                                    inputMode="numeric"
                                    value={total}
                                    onChange={(e) => setTotal(digits(e.target.value))}
                                    placeholder="Contoh: 30"
                                    className={`${inputCls} font-mono`}
                                />
                            </Field>

                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between">
                                    <label className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                                        {t.f5}
                                    </label>
                                    <span className="font-mono text-sm font-bold text-brand-violet" data-testid="rate-value">
                                        {rate.toFixed(2)}
                                    </span>
                                </div>
                                <input
                                    data-testid="input-rate"
                                    type="range"
                                    min="0.1"
                                    max="1"
                                    step="0.05"
                                    value={rate}
                                    onChange={(e) => setRate(+e.target.value)}
                                    className="rate-slider"
                                    style={{
                                        background: `linear-gradient(90deg, #FF6A5A, #B84BF0, #4E7CFF ${((rate - 0.1) / 0.9) * 100}%, var(--slider-track) ${((rate - 0.1) / 0.9) * 100}%)`,
                                    }}
                                />
                                <div className="flex justify-between font-mono text-[10px] text-zinc-600">
                                    <span>0.10</span>
                                    <span>0.55</span>
                                    <span>1.00</span>
                                </div>
                            </div>
                        </motion.div>

                        {/* Result */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            data-testid="panel-hasil"
                        >
                            <Tilt className="h-full">
                                <div className="relative flex h-full min-h-[380px] flex-col justify-between rounded-3xl border border-white/10 bg-[#0a0713]/80 p-8 backdrop-blur-xl sm:p-10">
                                    <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand-violet/20 blur-[90px]" />

                                    <div>
                                        <div className="mb-5 flex items-center justify-between">
                                            <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-500">
                                                <Receipt size={13} />
                                                {t.r1}
                                            </p>
                                            <SoundControls testId="sound-toggle" />
                                        </div>

                                        {hasil === null ? (
                                            <div data-testid="hasil-kosong">
                                                <p className="font-display text-5xl font-extrabold text-zinc-800 sm:text-6xl">—</p>
                                                <p className="mt-3 text-xs text-zinc-600">{t.r2}</p>
                                            </div>
                                        ) : (
                                            <AnimatedRupiah
                                                value={hasil}
                                                testId="hasil-refund"
                                                className="font-display text-5xl font-extrabold leading-none tracking-tight sm:text-6xl"
                                            />
                                        )}

                                        {hasil !== null && nama && (
                                            <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-semibold text-zinc-300" data-testid="hasil-aplikasi">
                                                <CircleDollarSign size={13} className="text-brand-violet" />
                                                {t.r3} {nama}
                                            </p>
                                        )}
                                    </div>

                                    <div className="mt-8 space-y-5">
                                        <div data-testid="progress-sisa">
                                            <div className="mb-2 flex justify-between text-xs text-zinc-400">
                                                <span>{t.r4}</span>
                                                <span className="font-mono text-zinc-500">
                                                    {sisaN} / {totalN || 0} {t.s9 === "days elapsed" ? "days" : "hari"}
                                                </span>
                                            </div>
                                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.06]">
                                                <motion.div
                                                    className="h-full rounded-full bg-gradient-to-r from-brand-coral via-brand-violet to-brand-blue"
                                                    animate={{ width: `${pct}%` }}
                                                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="row-per-hari">
                                                <p className="mb-1 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                                                    <Clock3 size={11} /> {t.r5}
                                                </p>
                                                <p className="font-mono text-sm font-bold text-white">
                                                    {perHari > 0 ? "Rp " + fmt(perHari) : "—"}
                                                </p>
                                            </div>
                                            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="row-fee">
                                                <p className="mb-1 flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                                                    <Percent size={11} /> {t.r6}
                                                </p>
                                                <p className="font-mono text-sm font-bold text-gradient">{(rate * 100).toFixed(0)}%</p>
                                            </div>
                                        </div>

                                        <p className="text-[10px] leading-relaxed text-zinc-600">{t.r7}</p>

                                        {hasil !== null && (
                                            <div data-testid="share-row">
                                                <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                                                    {t.s17}
                                                </p>
                                                <div className="flex flex-wrap gap-2.5">
                                                    <button onClick={() => shareWhatsApp(shareText)} data-testid="share-wa" className={shareBtn}>
                                                        <MessageCircle size={13} className="text-emerald-400" /> WhatsApp
                                                    </button>
                                                    <button onClick={() => shareTelegram(shareText)} data-testid="share-tg" className={shareBtn}>
                                                        <Send size={13} className="text-sky-400" /> Telegram
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            await copyText(shareText);
                                                            setCopied(true);
                                                            setTimeout(() => setCopied(false), 2000);
                                                        }}
                                                        data-testid="share-copy"
                                                        className={shareBtn}
                                                    >
                                                        {copied ? <Check size={13} className="text-emerald-400" /> : <Link2 size={13} />}
                                                        {copied ? t.s19 : t.s18}
                                                    </button>
                                                    <button onClick={unduhGambar} data-testid="share-image" className={shareBtn}>
                                                        <ImageDown size={13} className="text-brand-violet" /> {IMG_LABEL[lang] || IMG_LABEL.id}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Tilt>
                        </motion.div>
                    </div>
                ) : (
                    <SisaHari />
                )}

                <History
                    onMuat={(e) => {
                        setTab("refund");
                        setNama(e.nama || "");
                        setHarga(fmt(e.harga));
                        setTotal(String(e.total));
                        setSisa(String(e.sisa));
                        setRate(e.rate);
                    }}
                />
            </div>
        </section>
    );
}
